/* eslint-disable no-underscore-dangle */

import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import {
  Box,
  Breakpoint,
  Button,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme
} from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  AccordionSummaryProps
} from "@mui/material/AccordionSummary";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import DOMPurify from "dompurify";
import parse, { DOMNode, Element } from "html-react-parser";
import React, { useEffect, useState } from "react";
import Flag from "react-flagkit";
import { useTranslation } from "react-i18next";

import { generateLocaleTitleFromCode } from "@rooster/utils";

import { LegalType } from "../../types";

interface ITermsAndConditionsPopupProps {
  legal: LegalType[];
  open: boolean;
  onClose: () => void;
  /**
   * Optional controlled state for which language variant the user has
   * selected for each T&C document. Pass both `selectedLanguages` and
   * `onSelectedLanguagesChange` together, or neither (uncontrolled).
   * Passing only one leaves the Select unresponsive.
   */
  selectedLanguages?: Record<string, string>;
  onSelectedLanguagesChange?: (next: Record<string, string>) => void;
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  position: "relative",
  "&:not(:last-child)": {
    borderBottom: 0
  },
  "&:before": {
    display: "none"
  }
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)"
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1)
  }
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)"
}));

// Legacy content entries have no `status` field — treat those as Published
// so pre-existing translations keep rendering.
export const isPublished = (c?: { status?: string }) =>
  (c?.status ?? "Published") === "Published";

export const pickDefaultLanguage = (
  languages: string[],
  i18nLanguage?: string
): string => {
  if (languages.length === 0) return "en-US";
  if (i18nLanguage) {
    const exact = languages.find((l) => l === i18nLanguage);
    if (exact) return exact;
    const base = i18nLanguage.split("-")[0];
    const baseMatch = languages.find((l) => l.split("-")[0] === base);
    if (baseMatch) return baseMatch;
  }
  return languages.find((l) => l === "en-US") ?? languages[0];
};

export const TermsAndConditionsPopup: React.FC<
  ITermsAndConditionsPopupProps
> = (props) => {
  const { open, onClose, legal } = props;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md" as Breakpoint));
  const [forceFullScreenState, setForceFullScreenState] = useState(false);
  const [openState, setOpenState] = React.useState(false);
  const [scroll] = React.useState<DialogProps["scroll"]>("paper");
  const [internalSelectedLanguages, setInternalSelectedLanguages] = useState<
    Record<string, string>
  >({});
  const selectedLanguages =
    props.selectedLanguages ?? internalSelectedLanguages;
  // Treat as fully controlled only when both props are provided. If only the
  // callback is supplied (observer mode), update the internal state AND
  // notify the parent — otherwise the displayed value falls back to the
  // never-updated internal map and the <Select> appears stuck.
  const isFullyControlled =
    props.selectedLanguages !== undefined &&
    props.onSelectedLanguagesChange !== undefined;
  const setSelectedLanguages = (
    updater: (prev: Record<string, string>) => Record<string, string>
  ) => {
    if (isFullyControlled) {
      props.onSelectedLanguagesChange?.(updater(selectedLanguages));
    } else {
      // Compute the next value inside the functional setState reducer so the
      // observer callback sees the same value the reducer commits, even
      // under batched updates. Reading `internalSelectedLanguages` from the
      // render closure would lag by one update after rapid successive
      // changes.
      setInternalSelectedLanguages((prev) => {
        const next = updater(prev);
        props.onSelectedLanguagesChange?.(next);
        return next;
      });
    }
  };

  React.useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      if (
        props.selectedLanguages !== undefined &&
        props.onSelectedLanguagesChange === undefined
      ) {
        console.warn(
          "[TermsAndConditionsPopup] `selectedLanguages` was provided without `onSelectedLanguagesChange`. The Select will not respond to user input."
        );
      }
      if (
        props.selectedLanguages === undefined &&
        props.onSelectedLanguagesChange !== undefined
      ) {
        console.warn(
          "[TermsAndConditionsPopup] `onSelectedLanguagesChange` was provided without `selectedLanguages`. Component will manage state internally and forward changes via the callback."
        );
      }
    }
  }, [props.selectedLanguages, props.onSelectedLanguagesChange]);

  const handleClose = () => {
    setOpenState(false);
    onClose();
  };

  // LegalType["content"] doesn't (yet) declare `status` locally, so widen
  // via assertion to the shape isPublished actually reads at runtime.
  const publishedContent = (entry?: LegalType) =>
    (
      (entry?.content ?? []) as Array<{
        _id: string;
        language: string;
        body: string;
        status?: string;
      }>
    ).filter(isPublished);

  const descriptionElementRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  useEffect(() => {
    setOpenState(open);
  }, [open]);

  // Seed each document's default language from the browser language once
  // the popup opens, without clobbering a language the user already picked.
  useEffect(() => {
    if (!open) return;
    setSelectedLanguages((prev) => {
      const next = { ...prev };
      for (const entry of legal ?? []) {
        if (next[entry._id]) continue;
        const langs = publishedContent(entry).map((c) => c.language);
        next[entry._id] = pickDefaultLanguage(langs, i18n?.language);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, legal, i18n?.language]);

  return (
    <Dialog
      open={openState}
      onClose={handleClose}
      fullScreen={forceFullScreenState ?? fullScreen}
      maxWidth={"md" as Breakpoint}
      // Pin the dialog to its max width so a short/empty T&C body can't
      // collapse the box to a stub (R495-551 follow-up).
      fullWidth
      scroll={scroll}
      aria-labelledby="scroll-dialog-title"
      data-testid="terms-conditions-dialog"
    >
      <DialogTitle id="scroll-dialog-title" data-testid="terms-dialog-title">
        {t("ROOSTER.LINKS.TERMS_AND_CONDITIONS", {
          defaultValue: "Terms and Conditions"
        })}
      </DialogTitle>
      <DialogContent
        dividers={scroll === "paper"}
        sx={{
          // Keep a stable body height so sparse content doesn't shrink the
          // dialog vertically; let fullscreen flow naturally.
          minHeight: forceFullScreenState || fullScreen ? undefined : "50vh",
          scrollbarColor:
            theme.palette.mode === "dark" ? "gray rgba(18, 18, 18, 0.3)" : ""
        }}
        data-testid="terms-dialog-content"
      >
        <DialogContentText
          component="div"
          id="Terms and Conditions"
          ref={descriptionElementRef}
          tabIndex={-1}
          data-testid="terms-dialog-content-text"
        >
          {legal?.map((termsAndConditionFiltered, index) => {
            const publishedLanguages = publishedContent(
              termsAndConditionFiltered
            );
            // Shared by both the Select's displayed value and the rendered
            // body fallback so they never disagree before the seeding
            // effect commits `selectedLanguages` (M5).
            const effectiveLanguage =
              selectedLanguages[termsAndConditionFiltered._id] ??
              pickDefaultLanguage(
                publishedLanguages.map((c) => c.language),
                i18n?.language
              );
            return (
              <Accordion
                defaultExpanded
                key={termsAndConditionFiltered.name}
                slotProps={{
                  region: {
                    "aria-label": `${termsAndConditionFiltered?.name ?? "Terms"} section ${index + 1}`,
                    "aria-labelledby": undefined
                  }
                }}
                data-testid={`terms-accordion-${index}`}
              >
                <AccordionSummary
                  aria-controls={`panel-${index}-${termsAndConditionFiltered?.name?.replace(/\s+/g, "-")}-content`}
                  id={`panel-${index}-${termsAndConditionFiltered?.name?.replace(/\s+/g, "-")}-header`}
                  data-testid={`terms-accordion-summary-${index}`}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      minWidth: "100%"
                    }}
                    data-testid={`terms-accordion-header-${index}`}
                  >
                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontWeight: "bold",
                        maxWidth: "50%",
                        textOverflow: "ellipsis"
                      }}
                      data-testid={`terms-accordion-title-${index}`}
                    >
                      {termsAndConditionFiltered?.name ?? ""}
                    </Typography>
                  </Box>
                </AccordionSummary>
                {/* Language selector positioned outside AccordionSummary to fix nested-interactive a11y violation */}
                <Box
                  sx={{
                    position: "absolute",
                    right: 40,
                    top: 8,
                    zIndex: 1
                  }}
                  data-testid={`terms-language-selector-container-${index}`}
                >
                  <Select
                    value={effectiveLanguage}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setSelectedLanguages((prev) => ({
                        ...prev,
                        [termsAndConditionFiltered._id]: e.target
                          .value as string
                      }));
                    }}
                    inputProps={{
                      "aria-label": t("ROOSTER.LINKS.SELECT_LANGUAGE_FOR", {
                        defaultValue: "Select language for {{name}}",
                        name: termsAndConditionFiltered?.name ?? "terms"
                      })
                    }}
                    data-testid={`terms-language-select-${index}`}
                  >
                    {publishedLanguages.map((c) => (
                      <MenuItem
                        value={c?.language ?? ""}
                        key={c._id}
                        data-testid={`terms-language-option-${c?.language}`}
                      >
                        <Box sx={{ alignItems: "center", display: "flex" }}>
                          <Flag
                            country={c?.language?.split("-")?.[1] ?? "US"}
                          />{" "}
                          &nbsp;{" "}
                          {generateLocaleTitleFromCode(c?.language ?? "")}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <AccordionDetails
                  id={`panel-${index}-${termsAndConditionFiltered?.name?.replace(/\s+/g, "-")}-content`}
                  aria-labelledby={`panel-${index}-${termsAndConditionFiltered?.name?.replace(/\s+/g, "-")}-header`}
                  sx={{ overflow: "auto" }}
                  data-testid={`terms-accordion-details-${index}`}
                >
                  {parse(
                    DOMPurify.sanitize(
                      (
                        publishedLanguages.find(
                          (c) => c.language === effectiveLanguage
                        ) ?? publishedLanguages.at(0)
                      )?.body ?? ""
                    ),
                    {
                      replace: (domNode: DOMNode) => {
                        if (
                          domNode instanceof Element &&
                          domNode.name === "img"
                        ) {
                          const { src, alt } = domNode.attribs || {};
                          const isSafeUrl =
                            src &&
                            (src.startsWith("https://") ||
                              src.startsWith("http://"));
                          if (!isSafeUrl) {
                            return null;
                          }
                          return (
                            <img
                              style={{ maxWidth: "80%" }}
                              src={src}
                              alt={
                                alt ||
                                t("ROOSTER.LINKS.TERMS_IMAGE_ALT", {
                                  defaultValue:
                                    "Terms and conditions illustration"
                                })
                              }
                            />
                          );
                        }
                        return undefined;
                      }
                    }
                  )}
                </AccordionDetails>
              </Accordion>
            );
          }) ?? null}
        </DialogContentText>
      </DialogContent>
      <DialogActions data-testid="terms-dialog-actions">
        {!fullScreen && (
          <Button
            onClick={() => setForceFullScreenState(!forceFullScreenState)}
            data-testid="terms-fullscreen-button"
          >
            {forceFullScreenState
              ? t("ROOSTER.LINKS.TAC_BUTTON_EXIT_FULLSCREEN", {
                  defaultValue: "Exit Fullscreen"
                })
              : t("ROOSTER.LINKS.TAC_BUTTON_ENTER_FULLSCREEN", {
                  defaultValue: "Fullscreen"
                })}
          </Button>
        )}
        <Button onClick={handleClose} data-testid="terms-close-button">
          {t("ROOSTER.LINKS.TAC_BUTTON_CLOSE", { defaultValue: "Close" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
